import type { Point2d } from ".."
import { findAngle, findOffsetAngle } from ".."
import { adjacentEdgesToPoints } from "./adjacentEdgesToPoints"
import type { InsetEdge } from "./getInsetEdges"
import type { SerialTag } from "./linkAllInsetEdges"
import type { InsetLinkEdge } from "./linkInsetEdges"
import type { Edge2dComputed } from "../../mesh"

type SimplifyInsetEdgesStepResult = {
  result: SimplifyInsetEdgesResult
  procedure: { nodesEliminated: Point2d[] }
} | null

export const SimplifyInsetEdges_STEP = (
  options: SimplifyInsetEdgesOptions,
): {
  result: SimplifyInsetEdgesResult
  procedure: { nodesEliminated: Point2d[] }
} | null => {
  const result = { insetEdgesSimplified: options.insetsAndLinks }
  const procedure: SimplifyInsetEdgesProcedure = { nodesEliminated: [] }
  for (let i = 0; i < options.insetsAndLinks.length; i++) {
    const j = i + 1 === options.insetsAndLinks.length ? 0 : i + 1

    const a = options.insetsAndLinks[i]
    const b = options.insetsAndLinks[j]

    const { p0, p1, p2 } = adjacentEdgesToPoints(a, b)

    const angle = findAngle(p0, p1)
    const angleBetweenEdges = findOffsetAngle(p1, p2, angle)
    const isNearStraight =
      angleBetweenEdges < 0.0001 ||
      Math.abs(angleBetweenEdges - Math.PI * 2) < 0.0001

    if (isNearStraight) {
      const replacementEdge: InsetSimplifiedEdge & SerialTag = {
        p1: p0,
        p2: p2,
        type: `inset-simplified`,
        alphaCode: `${a.alphaCode}+${b.alphaCode}`,
        lowerBound: a.lowerBound,
        upperBound: b.upperBound,
        range: b.upperBound - a.lowerBound,
      }
      result.insetEdgesSimplified.splice(i, 2, replacementEdge)
      procedure.nodesEliminated.push(p1)

      return { result, procedure }
    }
  }

  return null
}

export type InsetSimplifiedEdge = Edge2dComputed & {
  type: "inset-simplified"
}

export type SimplifyInsetEdgesOptions = {
  insetsAndLinks: (
    | (InsetEdge & SerialTag)
    | (InsetLinkEdge & SerialTag)
    | (InsetSimplifiedEdge & SerialTag)
  )[]
}
export type SimplifyInsetEdgesResult = {
  insetEdgesSimplified: (
    | (InsetEdge & SerialTag)
    | (InsetLinkEdge & SerialTag)
    | (InsetSimplifiedEdge & SerialTag)
  )[]
}
export type SimplifyInsetEdgesProcedure = {
  nodesEliminated: Point2d[]
}

export type SimplifyInsetEdges = (options: SimplifyInsetEdgesOptions) => {
  result: SimplifyInsetEdgesResult
  procedure: SimplifyInsetEdgesProcedure
}

export const simplifyInsetEdges: SimplifyInsetEdges = (options) => {
  const result = { insetEdgesSimplified: options.insetsAndLinks }
  const procedure: SimplifyInsetEdgesProcedure = { nodesEliminated: [] }
  let next: SimplifyInsetEdgesStepResult = null
  let stepNumber = 0

  while ((next || stepNumber === 0) && stepNumber < 100) {
    stepNumber++
    next = SimplifyInsetEdges_STEP({
      insetsAndLinks: [...result.insetEdgesSimplified],
    })
    if (next) {
      result.insetEdgesSimplified = next.result.insetEdgesSimplified
      procedure.nodesEliminated.push(...next.procedure.nodesEliminated)
    }
  }
  if (stepNumber === 100) {
    throw new Error(`simplifyInsetEdges: cycle limit reached`)
  }

  return { result, procedure }
}
